import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ClipboardList,
  GraduationCap,
  Info,
  Search,
  User,
} from "lucide-react";
import { PortalTranscriptDocument } from "../PortalTranscriptDocument";
import { API_BASE } from "../apiConfig";

const ICON_STROKE = 1.5;

function resolveStudentImageUrl(raw, apiBase) {
  if (raw == null || String(raw).trim() === "") return null;
  const u = String(raw).trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/") && apiBase) return `${apiBase}${u}`;
  return u;
}

function StudentReportProfile({ student, apiBase, imageFailed, onImageError }) {
  const photoSrc = resolveStudentImageUrl(student?.imageUrl, apiBase);
  const initials =
    `${student?.firstName?.[0] ?? ""}${student?.lastName?.[0] ?? ""}`.toUpperCase() ||
    "?";

  return (
    <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-start">
      <div className="relative h-28 w-28 shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm sm:h-32 sm:w-32">
        {photoSrc && !imageFailed ? (
          <img
            src={photoSrc}
            alt=""
            className="h-full w-full object-cover"
            width={128}
            height={128}
            loading="lazy"
            decoding="async"
            onError={onImageError}
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-200 to-slate-100 text-2xl font-semibold text-slate-600"
            aria-hidden
          >
            {initials}
          </div>
        )}
      </div>
      <dl className="grid min-w-0 flex-1 grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="font-semibold text-slate-500">ID</dt>
        <dd className="text-slate-900">{student?.studentId}</dd>
        <dt className="font-semibold text-slate-500">Name</dt>
        <dd className="text-slate-900">
          {student?.firstName} {student?.lastName}
        </dd>
        <dt className="font-semibold text-slate-500">Department</dt>
        <dd className="text-slate-900">
          {student?.department?.code} — {student?.department?.name}
        </dd>
        {student?.admissionDate ? (
          <>
            <dt className="font-semibold text-slate-500">Admission</dt>
            <dd className="text-slate-900">
              {new Date(student.admissionDate).toLocaleDateString()}
            </dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

export function StudentTranscriptPage() {
  const [studentIdInput, setStudentIdInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  const [profileImageError, setProfileImageError] = useState(false);

  useEffect(() => {
    setProfileImageError(false);
  }, [report?.student?.studentId]);

  const fetchReport = async (e) => {
    e?.preventDefault();
    const id = studentIdInput.trim().replace(/\u200B/g, "").replace(/\s+/g, "");
    setError("");
    setReport(null);
    if (!id) {
      setError("Enter your student ID (e.g. STD-2026-0001).");
      return;
    }
    setLoading(true);
    try {
      const url = `${API_BASE}/api/public/student-academic-report?studentId=${encodeURIComponent(
        id
      )}`;
      const res = await fetch(url);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Request failed (${res.status})`);
        return;
      }
      setReport(data);
    } catch {
      setError(
        "Network error. Check that the university API is running and CORS is allowed."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main id="main" className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
      <nav className="mb-6 text-sm text-slate-600">
        <Link to="/" className="font-medium text-maroon-700 hover:text-maroon-800">
          ← Back to home
        </Link>
      </nav>

      <section
        className="relative mb-10 overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-5 shadow-soft sm:mb-12 sm:p-8"
        aria-labelledby="hero-heading"
      >
        <div
          className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-brand-500/15 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-maroon-500/15 blur-3xl"
          aria-hidden
        />

        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-maroon-700 to-maroon-900 text-white shadow-lg shadow-maroon-900/30 sm:h-14 sm:w-14">
            <GraduationCap
              className="h-7 w-7 sm:h-8 sm:w-8"
              strokeWidth={ICON_STROKE}
              aria-hidden
            />
          </span>
          <div className="min-w-0 flex-1">
            <h1
              id="hero-heading"
              className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl"
            >
              Student results &amp; transcript
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
              Look up your approved examination records and GPA using your official student
              ID. Information below is read-only and matches the university system.
            </p>
          </div>
        </div>

        <form className="relative mt-6" onSubmit={fetchReport}>
          <label
            htmlFor="student-id"
            className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600 sm:text-sm sm:normal-case sm:tracking-normal"
          >
            Student ID
          </label>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <div className="relative min-w-0 flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                strokeWidth={ICON_STROKE}
                aria-hidden
              />
              <input
                id="student-id"
                name="studentId"
                type="text"
                autoComplete="username"
                placeholder="e.g. STD-2026-0001"
                value={studentIdInput}
                onChange={(e) => setStudentIdInput(e.target.value)}
                className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-base text-slate-900 shadow-inner shadow-slate-900/5 placeholder:text-slate-400 focus:border-maroon-600 focus:outline-none focus:ring-2 focus:ring-maroon-600/25"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-xl bg-maroon-700 px-5 text-sm font-semibold text-white shadow-md shadow-maroon-900/25 transition hover:bg-maroon-800 disabled:cursor-not-allowed disabled:opacity-60 sm:px-6"
            >
              {loading ? (
                "Loading…"
              ) : (
                <>
                  View report
                  <ArrowRight className="h-5 w-5" strokeWidth={ICON_STROKE} aria-hidden />
                </>
              )}
            </button>
          </div>
          {error ? (
            <p className="mt-3 flex items-start gap-2 text-sm text-red-600" role="alert">
              <Info className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={ICON_STROKE} aria-hidden />
              <span>{error}</span>
            </p>
          ) : null}
        </form>
      </section>

      {report ? (
        <section className="mb-10 space-y-5 sm:mb-12" aria-labelledby="report-heading">
          <h2
            id="report-heading"
            className="flex items-center gap-2 border-b-2 border-maroon-200 pb-2 text-lg font-bold text-slate-900 sm:text-xl"
          >
            <ClipboardList
              className="h-6 w-6 text-maroon-700"
              strokeWidth={ICON_STROKE}
              aria-hidden
            />
            Your academic report
          </h2>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card sm:p-6">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-900 sm:text-base">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                <User className="h-5 w-5" strokeWidth={ICON_STROKE} aria-hidden />
              </span>
              Student profile
            </h3>
            <StudentReportProfile
              student={report.student}
              apiBase={API_BASE}
              imageFailed={profileImageError}
              onImageError={() => setProfileImageError(true)}
            />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-2 shadow-card sm:p-3">
            <PortalTranscriptDocument
              student={report.student}
              examinationRecords={report.examinationRecords}
              transcript={report.transcript}
              semesterSortMap={report.semesterSortMap}
              apiBase={API_BASE}
            />
          </div>
        </section>
      ) : null}

      <p className="mt-8 text-center text-xs text-slate-500">
        Student records are read-only and sourced from the official public API.
      </p>
    </main>
  );
}
